import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { useAuth } from "../hooks/useAuth";
import "./Header.css";

const Header: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, isDevBypass, refreshSession } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isDevelopment = import.meta.env.DEV;

  // Check if we're on allowed domains
  const currentHostname = window.location.hostname;
  const allowedDevDomains = [
    "dev.gesslargardens.com",
    "stage.gesslargardens.com",
  ];
  const isAllowedDomain = allowedDevDomains.includes(currentHostname);
  const isCloudFrontDomain = currentHostname.endsWith("cloudfront.net");

  // Show dev badge in development mode OR on allowed domains OR on CloudFront domains
  const shouldShowDevBadge =
    (isDevelopment || isAllowedDomain || isCloudFrontDomain) && isDevBypass;

  // Determine environment label for badge
  let envLabel = "DEV";
  if (isAllowedDomain) {
    envLabel = currentHostname.startsWith("dev.") ? "DEV" : "STAGE";
  } else if (isCloudFrontDomain) {
    envLabel = "CLOUD";
  }

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogoClick = () => {
    navigate("/");
  };

  const handleLogoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate("/");
    }
  };

  const handleLogout = async () => {
    await logout();
    await refreshSession();
    navigate("/", { replace: true });
  };

  const handleContactClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate("/contact");
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <header className="header" role="banner">
      <div className="container">
        <div
          className="logo"
          onClick={handleLogoClick}
          onKeyDown={handleLogoKeyDown}
          tabIndex={0}
          role="button"
          aria-label="Gesslar Gardens, go to home page"
        >
          <div className="logo-container">
            <img
              src="/images/gesslar-gardens-logo-bug.png"
              alt="" /* Alt text empty as the company name is in the next element */
              className="header-logo-icon"
              aria-hidden="true"
            />
            <div className="logo-text">
              <span className="logo-text-primary">GESSLAR</span>
              <span className="logo-text-secondary">GARDENS</span>
            </div>
          </div>
          {shouldShowDevBadge && (
            <span
              className="dev-badge"
              aria-label="Development authentication enabled"
              style={{
                fontSize: "0.6rem",
                backgroundColor: "#fd3d3d",
                color: "white",
                padding: "2px 6px",
                borderRadius: "10px",
                marginLeft: "8px",
                position: "relative",
                top: "-8px",
                fontWeight: "bold",
              }}
            >
              {envLabel} AUTH
            </span>
          )}
        </div>

        <button
          className="mobile-menu-toggle"
          onClick={toggleMobileMenu}
          aria-expanded={mobileMenuOpen}
          aria-controls="main-navigation"
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          <i
            className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}
            aria-hidden="true"
          ></i>
        </button>

        <nav
          className={`nav ${mobileMenuOpen ? "open" : ""}`}
          id="main-navigation"
          aria-label="Main navigation"
        >
          <ul className="nav-list">
            <li className="nav-item">
              <Link to="/">{t("nav.features")}</Link>
            </li>
            <li className="nav-item">
              <Link to="/">{t("nav.about")}</Link>
            </li>
            <li className="nav-item">
              <Link to="/contact" onClick={handleContactClick}>
                {t("nav.contact")}
              </Link>
            </li>

            {isAuthenticated ? (
              <>
                <li className="nav-item">
                  <Link to="/dashboard">{t("nav.dashboard")}</Link>
                </li>
                <li className="nav-item">
                  <Link to="/admin">{t("nav.admin")}</Link>
                </li>
                <li className="nav-item auth-item">
                  <button
                    onClick={handleLogout}
                    className="auth-button logout-button"
                  >
                    {t("dashboard.logout")}
                  </button>
                </li>
              </>
            ) : (
              <li className="nav-item auth-item">
                <Link to="/login" className="auth-button login-button">
                  {t("nav.login")}
                </Link>
              </li>
            )}

            <li className="nav-item language-switcher-item">
              <LanguageSwitcher />
            </li>
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
